import React, { useState, useEffect, useRef,useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import InputForm from './InputForm';
import AllStudentsResultsDisplay from './AllStudentsResultsDisplay';
import FieldManager from './FieldManager';
import { getAuthHeader } from '../../utils/auth';

const Dashboard = ({ onLogout, username }) => {
  // const API_BASE_URL = 'http://127.0.0.1:5001/api/mdu';
  const API_BASE_URL = 'https://peakybalvinder.pythonanywhere.com/api/mdu';

  const navigate = useNavigate();

  const [currentStudentData, setCurrentStudentData] = useState(null);
  const [allStudentsData, setAllStudentsData] = useState([]);
  const [showInputForm, setShowInputForm] = useState(false);
  const [isEditingStudent, setIsEditingStudent] = useState(false);
  const [originalIndexNo, setOriginalIndexNo] = useState(null);
  const [isLeftMenuOpen, setIsLeftMenuOpen] = useState(true);

  const [activeView, setActiveView] = useState('form');

  const [currentPage, setCurrentPage] = useState(1);
  const [studentsPerPage] = useState(10);

  const [expandedStudents, setExpandedStudents] = useState(new Set());
  const hasInitialized = useRef(false);

  // keep existing username behaviour
  username = localStorage.getItem('user');

    // 🔹 Count unique candidates instead of raw rows (multiple semesters per candidate)
  const totalCandidates = useMemo(() => {
    const keys = new Set();
    (allStudentsData || []).forEach((s) => {
      if (s.indexNo) {
        keys.add(s.indexNo);
      } else if (s.candidateId) {
        keys.add(s.candidateId);
      } else if (s.id) {
        keys.add(s.id);
      }
    });
    return keys.size;
  }, [allStudentsData]);



  const fetchStudents = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/students`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
      });

      if (response.status === 401) {
        alert('Session expired or unauthorized. Please log in again.');
        navigate('/login');
        return;
      }

      const data = await response.json();

      if (data && data.message && data.message === 'No students found') {
        alert('No students found');
        setAllStudentsData([]);
        setCurrentPage(1);
        return;
      }

      if (!response.ok) {
        const errorMessage = data.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      setAllStudentsData(data);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error fetching students:', error);
      alert('Failed to fetch student data: ' + error.message);
    }
  };

  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showInputForm) {
      setExpandedStudents(new Set());
      hasInitialized.current = false;
    }
  }, [showInputForm]);

  const handleStudentSubmit = async (formData) => {
    try {
      if (isEditingStudent) {
        // index change flow
        if (formData.indexNo !== originalIndexNo) {
          const confirmChange = window.confirm(
            `You are changing the Index Number from ${originalIndexNo} to ${formData.indexNo}.\n` +
              `This will delete the old record and create a new one. Do you want to proceed?`
          );
          if (!confirmChange) return;

          // delete old
          const deleteResponse = await fetch(`${API_BASE_URL}/students/${originalIndexNo}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeader(),
            },
          });

          if (deleteResponse.status === 401) {
            alert('Session expired or unauthorized. Please log in again.');
            navigate('/login');
            return;
          }
          if (!deleteResponse.ok) {
            const errorData = await deleteResponse.json();
            throw new Error(errorData.message || 'Failed to delete old student record.');
          }
          alert(`Old record (Index No: ${originalIndexNo}) deleted successfully.`);

          // add new
          const addResponse = await fetch(`${API_BASE_URL}/students`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeader(),
            },
            body: JSON.stringify(formData),
          });

          if (addResponse.status === 401) {
            alert('Session expired or unauthorized. Please log in again.');
            navigate('/login');
            return;
          }
          if (!addResponse.ok) {
            const errorData = await addResponse.json();
            throw new Error(
              errorData.message || 'Failed to add new student record with updated index number.'
            );
          }
          alert(`New record (Index No: ${formData.indexNo}) added successfully.`);
        } else {
          // regular update
          const response = await fetch(`${API_BASE_URL}/students/${originalIndexNo}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeader(),
            },
            body: JSON.stringify(formData),
          });

          if (response.status === 401) {
            alert('Session expired or unauthorized. Please log in again.');
            navigate('/login');
            return;
          }
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to update student.');
          }
          alert('Student updated successfully!');
        }
      } else {
        // add new (including new semester row)
        const response = await fetch(`${API_BASE_URL}/students`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader(),
          },
          body: JSON.stringify(formData),
        });

        if (response.status === 401) {
          alert('Session expired or unauthorized. Please log in again.');
          navigate('/login');
          return;
        }

        const responseData = await response.json();

        if (!response.ok) {
          throw new Error(responseData.message || 'Failed to add student');
        }

        alert(responseData.message || 'Student / semester added successfully!');
      }

      // cleanup
      setCurrentStudentData(null);
      setIsEditingStudent(false);
      setOriginalIndexNo(null);
      setShowInputForm(false);
      fetchStudents();
    } catch (error) {
      console.error(`Error during student operation:`, error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleDeleteSemester = async (semRow) => {
  if (
    !window.confirm(
      `Delete Semester ${semRow.semester} result for ${semRow.candidateName}?`
    )
  ) {
    return;
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/students/${encodeURIComponent(
        semRow.indexNo
      )}?semester=${encodeURIComponent(semRow.semester)}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
      }
    );

    if (response.status === 401) {
      alert('Session expired or unauthorized. Please log in again.');
      navigate('/login');
      return;
    }

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to delete semester');
    }

    alert(data.message || 'Semester deleted successfully.');
    // refresh list
    fetchStudents();
  } catch (err) {
    console.error('Error deleting semester:', err);
    alert(`Error: ${err.message}`);
  }
};

  const handleDeleteStudent = async (studentId) => {
    if (
      !window.confirm(
        'Are you sure you want to delete this student record? This action cannot be undone.'
      )
    ) {
      return;
    }
    const studentToDelete = allStudentsData.find((student) => student.id === studentId);
    if (!studentToDelete) {
      alert('Error: Student not found for deletion.');
      return;
    }
    const indexNoToDelete = studentToDelete.indexNo;

    try {
      const response = await fetch(`${API_BASE_URL}/students/${indexNoToDelete}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
      });

      if (response.status === 401) {
        alert('Session expired or unauthorized. Please log in again.');
        navigate('/login');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete student');
      }

      const responseData = await response.json();
      alert(responseData.message || 'Student deleted successfully!');
      fetchStudents();
    } catch (error) {
      console.error('Error deleting student:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleOpenAddStudentForm = () => {
    setCurrentStudentData(null);
    setIsEditingStudent(false);
    setOriginalIndexNo(null);
    setShowInputForm(true);
  };

  const handleEditStudent = (studentId) => {
    const studentToEdit = allStudentsData.find((student) => student.id === studentId);

    if (studentToEdit) {
      setCurrentStudentData(studentToEdit);
      setIsEditingStudent(true);
      setOriginalIndexNo(studentToEdit.indexNo);
      setShowInputForm(true);
    } else {
      console.error('Student not found for editing:', studentId);
      alert('Error: Student data not found for editing. Please refresh and try again.');
    }
  };

  //  const handleAddSemester = (studentId) => {
  //   const baseRow = allStudentsData.find((s) => s.id === studentId);
  //   if (!baseRow) {
  //     alert('Error: Student not found to add semester.');
  //     return;
  //   }

  //   const indexNo = baseRow.indexNo;

  //   // find all rows for same candidate
  //   const sameCandidateRows = allStudentsData.filter(
  //     (s) => s.indexNo === indexNo
  //   );

  //   // find max existing semester number
  //   let maxSem = 0;
  //   sameCandidateRows.forEach((r) => {
  //     const n = parseInt(r.semester, 10);
  //     if (!isNaN(n) && n > maxSem) {
  //       maxSem = n;
  //     }
  //   });

  //   const nextSem = maxSem > 0 ? maxSem + 1 : 1;

  //   const newSemesterInitial = {
  //     ...baseRow,
  //     id: undefined, // ensure treated as NEW
  //     dmcNo: '',
  //     // keep only course text in classSelected; semester will be controlled by "semester"
  //     classSelected: baseRow.course || baseRow.classSelected || '',
  //     semester: String(nextSem),      // 🔥 pre-select NEXT semester
  //     examMonth: '',
  //     resultYear: '',
  //     result: '',
  //     supwGrade: '',
  //     subjects: [],                   // start fresh subjects
  //     isFinalSemester: false,
  //     previousYearTotal:
  //       baseRow.previousYearTotal || baseRow.previous_year_total || '',
  //   };

  //   setCurrentStudentData(newSemesterInitial);
  //   setIsEditingStudent(false);        // new semester row
  //   setOriginalIndexNo(baseRow.indexNo);
  //   setShowInputForm(true);
  // };

  // 🔴 ADD THIS in Dashboard component
const handleAddSemester = (baseSemesterId) => {
  const baseRow = allStudentsData.find((s) => s.id === baseSemesterId);
  if (!baseRow) {
    alert('Could not find base semester record for this candidate.');
    return;
  }

  // find all rows of this candidate
  const sameCandidateRows = allStudentsData.filter(
    (s) => s.indexNo === baseRow.indexNo
  );

  // compute next semester number
  const maxSem = sameCandidateRows.reduce((max, row) => {
    const n = parseInt(row.semester, 10) || 0;
    return n > max ? n : max;
  }, 0);
  const nextSem = String(maxSem + 1 || 1); // "1", "2", ...

  // Build initial data for new semester:
  // ✅ DMC No. & other candidate details are inherited from baseRow
  const newInitial = {
    ...baseRow,
    id: undefined,                // new row
    semester: nextSem,            // auto-select next semester
    examMonth: '',                // fresh for this sem
    resultYear: '',
    result: '',
    isFinalSemester: false,
    previousYearTotal: undefined,
    subjects: [],                 // start with blank subjects
  };

  setCurrentStudentData(newInitial);
  setIsEditingStudent(false);     // new semester = create, not update
  setOriginalIndexNo(baseRow.indexNo);
  setShowInputForm(true);
};

  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
  const currentStudents = allStudentsData.slice(indexOfFirstStudent, indexOfLastStudent);

  const totalPages = Math.ceil(allStudentsData.length / studentsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-red-50 via-slate-50 to-white">
      {/* SIDEBAR */}
      <aside
        className={`bg-[#520000] text-white flex-shrink-0 transition-all duration-300 ease-in-out
          ${isLeftMenuOpen ? 'w-64 px-5 py-6' : 'w-20 px-2 py-6'}
          hidden md:flex flex-col shadow-2xl`}
      >
        {/* Logo / Title */}
        <div
          className={`flex items-center mb-8 ${
            isLeftMenuOpen ? 'justify-start' : 'justify-center'
          }`}
        >
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white/10">
            <span className="material-symbols-outlined text-2xl text-yellow-300">school</span>
          </div>
          <div
            className={`ml-3 ${
              isLeftMenuOpen ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'
            } transition-all duration-300`}
          >
            <h3 className="text-lg font-extrabold tracking-wide">MDU Admin</h3>
            <p className="text-[11px] text-red-100/80">
              Result Management
            </p>
          </div>
        </div>

        {/* Menu */}
        <nav className="mt-4">
          <p
            className={`text-[11px] uppercase tracking-[0.15em] text-red-100/70 mb-3 ${
              isLeftMenuOpen ? 'pl-1' : 'text-center'
            }`}
          >
            Actions
          </p>
          <ul className="space-y-2">
            <li>
              <button
                onClick={handleOpenAddStudentForm}
                className={`flex items-center w-full py-2.5 px-3 rounded-xl text-sm font-medium
                  transition duration-200
                  ${
                    showInputForm
                      ? 'bg-white text-[#520000] shadow-md'
                      : 'bg-transparent hover:bg-white/10'
                  }`}
                title={isLeftMenuOpen ? 'Add New Student Details' : 'Add Student'}
              >
                <span className="material-symbols-outlined text-[20px] mr-2">
                  person_add
                </span>
                <span className={isLeftMenuOpen ? 'block' : 'hidden'}>
                  Add Student Details
                </span>
              </button>
            </li>
            <li>
              <button
                onClick={() => setShowInputForm(false)}
                className={`flex items-center w-full py-2.5 px-3 rounded-xl text-sm font-medium
                  transition duration-200
                  ${
                    !showInputForm
                      ? 'bg-white text-[#520000] shadow-md'
                      : 'bg-transparent hover:bg-white/10'
                  }`}
                title={isLeftMenuOpen ? 'View All Student Details' : 'View Students'}
              >
                <span className="material-symbols-outlined text-[20px] mr-2">group</span>
                <span className={isLeftMenuOpen ? 'block' : 'hidden'}>
                  View Student Details
                </span>
              </button>
            </li>
            {/* <li>
              <button
                onClick={() => setActiveView('fields')}
                className={`flex items-center px-4 py-2 rounded-full w-full mb-2 transition ${
                  activeView === 'fields'
                    ? 'bg-[#520000] text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="material-symbols-outlined mr-3">tune</span>
                <span className={isLeftMenuOpen ? 'block' : 'hidden'}>
                  Manage Candidate Fields
                </span>
              </button>
            </li> */}
          </ul>
        </nav>

        {/* Footer small info */}
        <div className="mt-auto pt-6 border-t border-white/10 text-[11px] text-red-100/80">
          <p className={isLeftMenuOpen ? 'block' : 'hidden'}>
            Secure panel for managing MDU exam results.
          </p>
        </div>
      </aside>

      {/* MAIN AREA */}
      <div className="flex-1 flex flex-col">
        {/* TOP BAR */}
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {/* Sidebar toggle (visible on all) */}
              <button
                onClick={() => setIsLeftMenuOpen(!isLeftMenuOpen)}
                className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 focus:outline-none focus:ring-2 focus:ring-red-400/70 transition duration-200 md:mr-1"
                title={isLeftMenuOpen ? 'Collapse Menu' : 'Expand Menu'}
              >
                <span className="material-symbols-outlined text-[22px]">menu</span>
              </button>

              <div className="flex flex-col">
                <h1 className="text-lg md:text-2xl font-extrabold text-[#520000] tracking-tight">
                  MDU Exam Dashboard
                </h1>
                <p className="text-xs md:text-sm text-gray-500">
                  Manage student records & generate detailed marksheets
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {username && (
                <span className="hidden sm:inline text-sm md:text-base font-medium text-gray-700">
                  Welcome,&nbsp;
                  <span className="text-red-700 font-semibold capitalize">
                    {JSON.parse(username).username || 'User'}
                  </span>
                  !
                </span>
              )}
              <button
                onClick={onLogout}
                className="bg-red-500 hover:bg-red-600 text-white font-semibold text-sm md:text-base py-2 px-4 md:px-6 rounded-full focus:outline-none focus:ring-2 focus:ring-red-300 transition duration-200 shadow-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </header>

        {/* CONTENT */}
        <main className="flex-1 overflow-y-auto px-4 md:px-8 py-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Overview strip */}
            <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="col-span-2 bg-white/80 border border-red-100 rounded-2xl px-5 py-4 shadow-sm flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-gray-400 mb-1">
                    Current View
                  </p>
                  <p className="text-sm font-semibold text-gray-800">
                    {showInputForm
                      ? isEditingStudent
                        ? 'Editing existing student / semester record'
                        : 'Adding new student / semester record'
                      : 'Viewing all student records'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-[0.2em] text-gray-400 mb-1">
                    Total Students
                  </p>
                  <p className="text-2xl font-bold text-[#520000]">
                    {totalCandidates}
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-red-600 to-red-800 rounded-2xl px-5 py-4 text-white shadow-md flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-red-100 mb-1">
                    Page
                  </p>
                  <p className="text-lg font-bold">
                    {totalPages ? `${currentPage} / ${totalPages}` : '1 / 1'}
                  </p>
                </div>
                <span className="material-symbols-outlined text-3xl opacity-80">
                  fact_check
                </span>
              </div>
            </section>

            {/* FORM OR TABLE */}
            {showInputForm ? (
              <section className="bg-white rounded-2xl shadow-lg border border-red-100/60 px-4 md:px-8 py-6 md:py-8 animate-fadeIn">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-extrabold text-[#520000]">
                      {isEditingStudent ? 'Edit Student / Semester Data' : 'Add New Student / Semester'}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Fill in all required fields carefully. These details will appear on the
                      marksheet.
                    </p>
                  </div>
                  {isEditingStudent && (
                    <button
                      onClick={() => {
                        setCurrentStudentData(null);
                        setIsEditingStudent(false);
                        setOriginalIndexNo(null);
                        setShowInputForm(false);
                      }}
                      className="hidden sm:inline bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-5 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-300 transition duration-200"
                    >
                      Cancel Edit
                    </button>
                  )}
                </div>

                <InputForm
                  initialData={currentStudentData}
                  onSubmit={handleStudentSubmit}
                  isEditing={isEditingStudent}
                />

                {isEditingStudent && (
                  <div className="sm:hidden flex justify-center mt-6">
                    <button
                      onClick={() => {
                        setCurrentStudentData(null);
                        setIsEditingStudent(false);
                        setOriginalIndexNo(null);
                        setShowInputForm(false);
                      }}
                      className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2.5 px-8 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-300 transition duration-200"
                    >
                      Cancel Edit
                    </button>
                  </div>
                )}
              </section>
            ) : (
              <section className="bg-white rounded-2xl shadow-lg border border-gray-100 px-3 md:px-6 py-5 md:py-6 animate-fadeIn">
                <AllStudentsResultsDisplay
                  students={currentStudents}
                  onEdit={handleEditStudent}
                  onDelete={handleDeleteStudent}
                  onDeleteSemester={handleDeleteSemester} 
                  onAddSemester={handleAddSemester}   // <<< NEW
                  expandedStudents={expandedStudents}
                  setExpandedStudents={setExpandedStudents}
                  hasInitialized={hasInitialized}
                />

                {totalPages > 1 && (
                  <nav className="flex flex-wrap justify-center mt-6 md:mt-8 gap-2">
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="py-2 px-4 rounded-full border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base transition duration-150"
                    >
                      Previous
                    </button>
                    {pageNumbers.map((number) => (
                      <button
                        key={number}
                        onClick={() => paginate(number)}
                        className={`py-2 px-4 rounded-full text-sm md:text-base transition duration-150 ${
                          currentPage === number
                            ? 'bg-red-600 text-white shadow-md'
                            : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                        }`}
                      >
                        {number}
                      </button>
                    ))}
                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="py-2 px-4 rounded-full border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base transition duration-150"
                    >
                      Next
                    </button>
                  </nav>
                )}
              </section>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;